import { useState } from "react";
import { Button, Form } from "react-bootstrap";
import "./LeaveRequest.scss";
import ConfirmModal from "../ConfirmModal/ConfirmModal";
import Validations from "../Validations/Validations";
import EditableCalendar from "../EditableCalendar/EditableCalendar";
import {
  useCreateLeaveRequestMutation,
  useSendLeaveRequestEmailMutation,
} from "store/conge/congeApi";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

export default function LeaveRequest() {
  const { t } = useTranslation();
  const [leaveSections, setLeaveSections] = useState([]);
  const [justification, setJustification] = useState(null);
  const [requeste, setRequeste] = useState(null);
  const [steps, setSteps] = useState([]);
  const [isRequestCompleted, setIsRequestCompleted] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const currentUser = useSelector((state) => state.auth.user);
  const [sendLeaveRequestEmail] = useSendLeaveRequestEmailMutation();
  const [comment, setComment] = useState("");
  const [show, setShow] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [createLeaveRequest] = useCreateLeaveRequestMutation();

  const addLeaveSection = () => {
    setLeaveSections([
      ...leaveSections,
      {
        id: Date.now(),
        type: "CP",
        description: "",
        editing: true,
        calendarNotes: {},
      },
    ]);
  };

  const updateLeave = (id, field, value) => {
    setLeaveSections((prev) =>
      prev.map((leave) =>
        leave.id === id ? { ...leave, [field]: value } : leave
      )
    );
  };
  const saveLeave = (id) => updateLeave(id, "editing", false);
  const cancelLeave = (id) =>
    setLeaveSections((prev) => prev.filter((l) => l.id !== id));
  const editLeave = (id) => updateLeave(id, "editing", true);
  const deleteLeave = (id) =>
    setLeaveSections((prev) => prev.filter((l) => l.id !== id));

  const handleConfirm = async () => {
    try {
      setLoadingEmail(true);
      const response = await sendLeaveRequestEmail(requeste).unwrap();
      toast.success(t("leaveRequest.email_sent_success"));
      setShow(false);
      setSteps([
        { name: currentUser.name, date: new Date(), signed: true },
        { name: response?.data?.validateur.name, date: null, signed: false },
      ]);
      setShowValidation(true);
      setIsRequestCompleted(true);
    } catch (error) {
      console.error(error);
      toast.error(t("leaveRequest.email_sent_error"));
    } finally {
      setLoadingEmail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append("comment", comment);
      formData.append("leaves", JSON.stringify(leaveSections));
      formData.append("supplier", currentUser?.supplierId);
      formData.append("collaborator", currentUser?._id);
      formData.append("validateur", currentUser?.validateur);
      if (justification) formData.append("justification", justification);
      const response = await createLeaveRequest(formData).unwrap();
      setRequeste(response.data);
      setShow(true);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="leave-request m-4">
      <div className="header d-flex justify-content-between align-items-center mb-3">
        <h5>{t("leaveRequest.title")}</h5>
        <div>
          {!loadingEmail && !isRequestCompleted && (
            <>
              <Button
                variant="outline-secondary"
                onClick={addLeaveSection}
                className="me-2">
                {t("leaveRequest.add_leave")}
              </Button>
              <Button variant="primary" onClick={handleSubmit}>
                {t("leaveRequest.save_request")}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="calendar-container1">
        {leaveSections.map((leave) => (
          <div key={leave.id} className="calendar-card mb-3">
            {leave.editing ? (
              <>
                <Form.Select
                  className="mb-2"
                  value={leave.type}
                  onChange={(e) =>
                    updateLeave(leave.id, "type", e.target.value)
                  }>
                  {Object.entries(t("leaveRequest.leave_types")).map(
                    ([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    )
                  )}
                </Form.Select>

                <Form.Control
                  className="mb-2"
                  type="text"
                  placeholder={t("leaveRequest.description")}
                  value={leave.description}
                  onChange={(e) =>
                    updateLeave(leave.id, "description", e.target.value)
                  }
                />

                <div className="dates">
                  <EditableCalendar
                    leaveId={leave.id}
                    initialNotes={leave.calendarNotes}
                    onChange={(id, notes) =>
                      updateLeave(id, "calendarNotes", notes)
                    }
                    readOnly={!leave.editing}
                  />
                </div>

                <div className="actions mt-3 text-end">
                  <Button
                    size="sm"
                    variant="success"
                    className="me-2"
                    onClick={() => saveLeave(leave.id)}>
                    {t("leaveRequest.actions.add")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => cancelLeave(leave.id)}>
                    {t("leaveRequest.actions.cancel")}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h6>{leave.type}</h6>
                  <div>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="me-1"
                      onClick={() => editLeave(leave.id)}>
                      {t("leaveRequest.actions.edit")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => deleteLeave(leave.id)}>
                      {t("leaveRequest.actions.delete")}
                    </Button>
                  </div>
                </div>
                <p className="mb-1">
                  <strong>{t("leaveRequest.description")} :</strong>{" "}
                  {leave.description || "-"}
                </p>

                <div className="dates">
                  <EditableCalendar
                    leaveId={leave.id}
                    initialNotes={leave.calendarNotes}
                    onChange={(id, notes) =>
                      updateLeave(id, "calendarNotes", notes)
                    }
                    readOnly={true}
                  />
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      <div className="footer mt-4">
        <Form.Group className="mb-3">
          <Form.Label>{t("leaveRequest.justification")}</Form.Label>
          <Form.Control
            type="file"
            onChange={(e) => setJustification(e.target.files[0])}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>{t("leaveRequest.comment")}</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </Form.Group>
      </div>

      {showValidation && (
        <div className="p-4">
          <Validations steps={steps} currentStep={2} />
        </div>
      )}

      <ConfirmModal
        show={show}
        handleClose={() => setShow(false)}
        handleConfirm={handleConfirm}
        showValidzation={showValidation}
        setShowValidation={setShowValidation}
        loadingEmail={loadingEmail}
      />
    </div>
  );
}
